import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Trash2, Pencil, ShieldAlert, ArrowLeft, Download, Mail, CheckCircle2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import AdminEditModal from "@/components/AdminEditModal";

const TABS = [
    { key: "listings", endpoint: "/admin/listings", labelKey: "admin_crud.tab_listings", csvType: "listings" },
    { key: "clubs", endpoint: "/admin/clubs", labelKey: "admin_crud.tab_clubs", csvType: "clubs" },
    { key: "club_interest", endpoint: "/admin/club-interest", labelKey: "admin_crud.tab_club_interest", csvType: "club_interest" },
    { key: "contact_messages", endpoint: "/admin/contact-messages", labelKey: "admin_crud.tab_contacts", csvType: "contact_messages" },
];

export default function AdminCrud() {
    const { t } = useTranslation();
    const { user, loading } = useAuth();
    const [tab, setTab] = useState("listings");
    const [items, setItems] = useState(null);
    const [error, setError] = useState(null);
    const [refresh, setRefresh] = useState(0);
    const [editing, setEditing] = useState(null);  // { tab, item } | null

    useEffect(() => {
        if (loading || !user || typeof user !== "object") return;
        let alive = true;
        (async () => {
            setItems(null);
            setError(null);
            try {
                const { data } = await api.get(
                    TABS.find((t_) => t_.key === tab).endpoint,
                    { params: { limit: 200 } },
                );
                if (alive) setItems(data);
            } catch (e) {
                const msg = formatApiError(e.response?.data?.detail);
                if (alive) setError(msg);
            }
        })();
        return () => { alive = false; };
    }, [tab, user, loading, refresh]);

    const deleteItem = async (item) => {
        const confirmMsg = t("admin_crud.confirm_delete", { name: item.name || item.title || item.email });
        if (!window.confirm(confirmMsg)) return;
        let endpoint;
        if (tab === "listings") endpoint = `/admin/listings/${item.id}`;
        else if (tab === "clubs") endpoint = `/admin/clubs/${item.id}`;
        else if (tab === "contact_messages") endpoint = `/admin/contact-messages/${item.id}`;
        else endpoint = `/admin/club-interest/${item.id}`;
        try {
            await api.delete(endpoint);
            toast.success(t("admin_crud.deleted"));
            setRefresh((x) => x + 1);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        }
    };

    const markStatus = async (item, status) => {
        try {
            await api.patch(`/admin/contact-messages/${item.id}`, { status });
            toast.success(t("admin_crud.status_updated"));
            setRefresh((x) => x + 1);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        }
    };

    const sendTestDigest = async () => {
        if (!window.confirm(t("admin_crud.confirm_test_digest"))) return;
        try {
            const { data } = await api.post("/admin/digest/send-test", {});
            if (data?.ok) toast.success(t("admin_crud.test_digest_sent"));
            else toast.error(`${t("admin_crud.test_digest_failed")}: ${data?.reason || "?"}`);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        }
    };

    const downloadCsv = async () => {
        const current = TABS.find((t_) => t_.key === tab);
        if (!current) return;
        try {
            const res = await api.get("/admin/export", {
                params: { type: current.csvType },
                responseType: "blob",
            });
            const url = URL.createObjectURL(res.data);
            const a = document.createElement("a");
            a.href = url;
            a.download = `recomendalibros-${current.csvType}.csv`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
            toast.success(t("admin_crud.downloaded"));
        } catch (e) {
            toast.error(t("admin_crud.csv_failed"));
        }
    };

    if (loading) return null;
    if (!user || typeof user !== "object") {
        return (
            <main className="min-h-[calc(100vh-73px)] flex flex-col items-center justify-center px-6 text-center">
                <ShieldAlert size={32} strokeWidth={1} className="text-[var(--accent)] mb-4" />
                <p className="font-display text-2xl text-[var(--ink)] mb-3">
                    {t("admin.login_required")}
                </p>
                <Link to="/acceder" className="bg-[var(--ink)] text-[var(--bg)] rounded-full hover:bg-[var(--accent)] transition-colors px-6 py-2 text-sm uppercase tracking-widest">
                    {t("nav.login")}
                </Link>
            </main>
        );
    }

    return (
        <main
            data-testid="admin-crud-page"
            className="min-h-[calc(100vh-73px)] max-w-7xl mx-auto px-5 md:px-12 py-12 md:py-16"
        >
            <Link
                to="/admin"
                data-testid="admin-crud-back"
                className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-[var(--ink-muted)] hover:text-[var(--accent)] mb-8"
            >
                <ArrowLeft size={14} strokeWidth={1.5} />
                {t("admin_crud.back_to_insights")}
            </Link>

            <h1
                className="font-display text-4xl md:text-5xl text-[var(--ink)] tracking-tight mb-3"
                style={{ fontVariationSettings: '"wght" 700, "wdth" 80, "opsz" 144' }}
            >
                {t("admin_crud.title")}
            </h1>

            <div className="mb-8">
                <button
                    type="button"
                    data-testid="admin-test-digest-btn"
                    onClick={sendTestDigest}
                    className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-[var(--accent)] border border-[var(--accent)] rounded-full px-4 py-1.5 hover:opacity-80 transition-opacity"
                >
                    <Mail size={12} strokeWidth={1.5} />
                    {t("admin_crud.send_test_digest")}
                </button>
            </div>

            <div className="flex items-center justify-between border-b border-[var(--border)] mb-8 gap-4 flex-wrap">
                <div className="flex items-center gap-6">
                    {TABS.map((tb) => (
                        <button
                            key={tb.key}
                            data-testid={`admin-crud-tab-${tb.key}`}
                            onClick={() => setTab(tb.key)}
                            className={`relative pb-3 text-sm uppercase tracking-widest transition-colors ${
                                tab === tb.key
                                    ? "text-[var(--ink)]"
                                    : "text-[var(--ink-muted)] hover:text-[var(--accent)]"
                            }`}
                        >
                            {t(tb.labelKey)}
                            {tab === tb.key && (
                                <span className="absolute left-0 right-0 -bottom-px h-0.5 bg-[var(--accent)]" />
                            )}
                        </button>
                    ))}
                </div>
                <button
                    type="button"
                    data-testid="admin-crud-csv-btn"
                    onClick={downloadCsv}
                    className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-[var(--accent)] border border-[var(--accent)] rounded-full px-4 py-1.5 mb-3 hover:opacity-80 transition-opacity"
                >
                    <Download size={12} strokeWidth={1.5} />
                    {t("admin_crud.download_csv")}
                </button>
            </div>

            {error && (
                <p className="text-[var(--danger)] text-sm mb-6">{error}</p>
            )}

            {items === null ? (
                <p className="text-[var(--ink-muted)] italic animate-slow-pulse">…</p>
            ) : items.length === 0 ? (
                <p className="text-[var(--ink-muted)]">{t("admin_crud.empty")}</p>
            ) : (
                <ul className="divide-y divide-[var(--border)]" data-testid="admin-crud-list">
                    {items.map((it) => (
                        <li
                            key={it.id}
                            data-testid={`admin-crud-item-${it.id}`}
                            className="flex items-start justify-between gap-4 py-4"
                        >
                            <div className="min-w-0 flex-1">
                                <p className="font-display text-lg text-[var(--ink)] truncate">
                                    {tab === "contact_messages" ? it.subject : (it.name || it.title || it.email)}
                                </p>
                                <p className="text-xs text-[var(--ink-dim)] truncate">
                                    {tab === "listings" ? (
                                        <>
                                            {it.author} · {it.price} {it.currency?.toUpperCase()} · {it.status}
                                        </>
                                    ) : tab === "clubs" ? (
                                        <>
                                            {it.kind} · {it.language} · {it.member_count ?? 0} {t("clubs.members")}
                                        </>
                                    ) : tab === "contact_messages" ? (
                                        <>
                                            <span className={`inline-block px-2 py-0.5 mr-2 rounded-full text-[9px] uppercase tracking-widest ${it.status === "new" ? "bg-[var(--accent)] text-[var(--bg)]" : "bg-[var(--border)] text-[var(--ink-muted)]"}`}>
                                                {it.status || "new"}
                                            </span>
                                            {it.name} · {it.email} · {(it.message || "").slice(0, 80)}{(it.message || "").length > 80 ? "…" : ""}
                                        </>
                                    ) : (
                                        <>
                                            {it.email} · {it.submissions ?? 1}x · {(it.wishlist || "").slice(0, 80)}{(it.wishlist || "").length > 80 ? "…" : ""}
                                        </>
                                    )}
                                </p>
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0">
                                {tab === "contact_messages" && it.status === "new" && (
                                    <button
                                        data-testid={`admin-mark-replied-${it.id}`}
                                        onClick={() => markStatus(it, "replied")}
                                        aria-label="Mark replied"
                                        className="flex items-center gap-1.5 text-[var(--ink-dim)] hover:text-[var(--accent)] transition-colors px-3 py-1 border border-[var(--border)] rounded-full"
                                    >
                                        <CheckCircle2 size={14} strokeWidth={1.5} />
                                        <span className="text-xs uppercase tracking-widest">
                                            {t("admin_crud.mark_replied")}
                                        </span>
                                    </button>
                                )}
                                {(tab === "listings" || tab === "clubs") && (
                                    <button
                                        data-testid={`admin-edit-${it.id}`}
                                        onClick={() => setEditing({ tab, item: it })}
                                        aria-label="Edit"
                                        className="flex items-center gap-1.5 text-[var(--ink-dim)] hover:text-[var(--accent)] transition-colors px-3 py-1 border border-[var(--border)] rounded-full"
                                    >
                                        <Pencil size={14} strokeWidth={1.5} />
                                        <span className="text-xs uppercase tracking-widest">
                                            {t("admin_crud.edit")}
                                        </span>
                                    </button>
                                )}
                                <button
                                    data-testid={`admin-delete-${it.id}`}
                                    onClick={() => deleteItem(it)}
                                    aria-label="Delete"
                                    className="flex items-center gap-1.5 text-[var(--ink-dim)] hover:text-[var(--danger)] transition-colors px-3 py-1 border border-[var(--border)] rounded-full"
                                >
                                    <Trash2 size={14} strokeWidth={1.5} />
                                    <span className="text-xs uppercase tracking-widest">
                                        {t("admin_crud.delete")}
                                    </span>
                                </button>
                            </div>
                        </li>
                    ))}
                </ul>
            )}

            {editing && (
                <AdminEditModal
                    tab={editing.tab}
                    item={editing.item}
                    onClose={() => setEditing(null)}
                    onSaved={() => setRefresh((x) => x + 1)}
                />
            )}
        </main>
    );
}
